import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../../theme';
import EditIcon from '@mui/icons-material/Edit';
import Header from '../../../components/Header';
import ConfirmDeleteDialog from '../../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import { can, filterActionColumns } from '../../../utils/permissions';

function BookingsLoadingOverlay() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        gap: 1.5,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <CircularProgress size={44} sx={{ color: colors.grey[100] }} />
      <Typography variant="body2" sx={{ color: colors.grey[100], fontWeight: 500 }}>
        Loading bookings...
      </Typography>
    </Box>
  );
}

const AllBookings = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'fullName', sort: 'asc' }]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [bookingDateFilter, setBookingDateFilter] = useState('');
  const [guideFilter, setGuideFilter] = useState('');
  const [guides, setGuides] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingIdToDelete, setBookingIdToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  const token = localStorage.getItem('token');
  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  const isCoordinator = Number(adminUser?.role) === 3;

  const getGuides = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/all-names`, { headers: { token } });
      const data = await res.json();
      if (data.success && data.guides) {
        setGuides(data.guides);
      }
    } catch (e) {
      console.error('Error fetching guides for filter', e);
    }
  };

  useEffect(() => {
    getGuides();
  }, []);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchRequestIdRef = useRef(0);
  const abortRef = useRef(null);

  const fetchBookings = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++fetchRequestIdRef.current;

    setLoading(true);

    const sortField = sortModel[0]?.field || 'fullName';
    const sortOrder = (sortModel[0]?.sort || 'asc').toUpperCase();
    const filterValue = {};
    if (guideFilter) {
      filterValue.guideId = guideFilter;
    }
    if (bookingDateFilter) {
      filterValue.bookingStartDate = bookingDateFilter;
    }

    const params = new URLSearchParams({
      page: String(page),
      limit: String(pageSize),
      sortBy: sortField,
      sortOrder,
      filter: JSON.stringify(filterValue),
      search,
    });

    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings?${params.toString()}`, {
        headers: { token },
        signal: controller.signal,
      });
      const data = await res.json();
      if (requestId !== fetchRequestIdRef.current) return;
      if (data.success) {
        setAllBookings(data.data);
        setTotalCount(data?.pagination?.total);
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Error fetching bookings', e);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, sortModel, search, bookingDateFilter, guideFilter, token]);

  // Function to convert backend date to 'YYYY-MM-DD' format
  const convertDate = (backendDate) => {
    const dateObject = new Date(backendDate);
    const day = String(dateObject.getDate()).padStart(2, '0');
    const month = String(dateObject.getMonth() + 1).padStart(2, '0');
    const year = dateObject.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchBookings();
    return () => abortRef.current?.abort();
  }, [fetchBookings]);

  // Delete Booking
  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/${id}`, {
      method: 'DELETE',
      headers: { token },
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        fetchBookings();
      }
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setBookingIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setBookingIdToDelete(null);
  };

  const handleConfirmDeleteBooking = async () => {
    if (bookingIdToDelete == null) return;
    const id = bookingIdToDelete;
    await handleDelete(id);
    handleCloseDeleteDialog();
  };

  const handleSelectAllAcrossPages = async () => {
    setSelectAllLoading(true);
    const filterValue = {};
    if (guideFilter) filterValue.guideId = guideFilter;
    if (bookingDateFilter) filterValue.bookingStartDate = bookingDateFilter;

    const params = new URLSearchParams({ filter: JSON.stringify(filterValue), search });
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/ids?${params.toString()}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success) {
        setSelectedRows(data.ids);
        setSelectAllMode(true);
      }
    } catch (e) {
      console.error('Error fetching all booking IDs', e);
    }
    setSelectAllLoading(false);
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
    setSelectAllMode(false);
  };

  const handleBulkDelete = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/bookings/bulk`, {
      method: 'DELETE',
      headers: { token, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: selectedRows }),
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) {
        setSelectedRows([]);
        fetchBookings();
      }
    }
    setBulkDeleteDialogOpen(false);
  };

  const baseColumns = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true,
      filterable: false,
      sortable: false,
    },
    {
      field: 'fullName',
      headerName: 'Passenger',
      flex: 1,
      minWidth: 120,
      cellClassName: 'name-column--cell',
      valueGetter: (params) => `${params.row.passengerName || ''} ${params.row.passengerSurname || ''}`,
      renderCell: (params) => (
        <div>{params.row.passengerName ? `${params.row.passengerName} ${params.row.passengerSurname}` : '-'}</div>
      ),
    },
    {
      field: 'guideFullName',
      headerName: 'Guide',
      flex: 1,
      minWidth: 100,
      sortable: false,
      valueGetter: (params) => `${params.row.guideName || ''} ${params.row.guideSurname || ''}`,
      renderCell: (params) => <div>{params.value?.trim() ? params.value : '-'}</div>,
    },
    {
      field: 'destination',
      headerName: 'Destination',
      flex: 1,
      minWidth: 100,
      cellClassName: 'name-column--cell',
    },
    {
      field: 'bookingStartDate',
      headerName: 'Booking Date',
      type: 'date',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => <div>{params.row.bookingStartDate ? convertDate(params.row.bookingStartDate) : '-'}</div>,
    },
  ];

  const coordinatorOnlyColumns = [
    {
      field: 'totalExcursions',
      headerName: 'Total Excursions',
      flex: 1,
      minWidth: 120,
      type: 'number',
      renderCell: (params) => <div>{params.row.totalExcursions ?? 0}</div>,
    },
    {
      field: 'totalPaid',
      headerName: 'Total Paid (€)',
      flex: 1,
      minWidth: 100,
      type: 'number',
      renderCell: (params) => (
        <div>€ {Number(params.row.totalPaid ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      ),
    },
    {
      field: 'totalScanned',
      headerName: 'Total Scanned',
      flex: 1,
      minWidth: 110,
      type: 'number',
      renderCell: (params) => <div>{params.row.totalScanned ?? 0}</div>,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      minWidth: 110,
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/booking/${cellValues.row.id}`} style={{ textDecoration: 'none' }}>
          <Button
            size="small"
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
            }}
            variant="contained"
          >
            View more
          </Button>
        </Link>
      ),
    },
  ];

  const contractNoColumn = {
    field: 'contractNo',
    headerName: 'Contract No',
    flex: 1,
    minWidth: 70,
    filterable: false,
  };

  const adminExtraColumns = [
    {
      field: 'bus',
      headerName: 'Bus',
      flex: 1,
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'View',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/booking/${cellValues.row.id}`}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[700] },
            }}
            variant="contained"
          >
            <VisibilityIcon />
          </Button>
        </Link>
      ),
    },
    {
      field: 'Edit',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/booking/${cellValues.row.id}/update`}>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.greenAccent[700] },
            }}
            variant="contained"
          >
            <EditIcon />
          </Button>
        </Link>
      ),
    },
    {
      field: 'Delete',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.redAccent[500],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.redAccent[600] },
          }}
          variant="contained"
          onClick={() => handleOpenDeleteDialog(cellValues.row.id)}
        >
          <DeleteIcon />
        </Button>
      ),
    },
  ];

  const allPageSelected = allBookings.length > 0 && allBookings.every((r) => selectedRows.includes(r.id));
  const somePageSelected = selectedRows.length > 0 && !allPageSelected;

  const selectColumn = {
    field: '__select__',
    headerName: '',
    width: 50,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderHeader: () => (
      <Checkbox
        checked={selectAllMode || allPageSelected}
        indeterminate={!selectAllMode && somePageSelected}
        disabled={selectAllLoading}
        sx={{ color: `${colors.greenAccent[200]} !important` }}
        onChange={(e) => {
          if (e.target.checked) {
            handleSelectAllAcrossPages();
          } else {
            handleClearSelection();
          }
        }}
      />
    ),
    renderCell: (params) => (
      <Checkbox
        checked={selectedRows.includes(params.row.id)}
        sx={{ color: `${colors.greenAccent[200]} !important` }}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRows((prev) => [...prev, params.row.id]);
          } else {
            setSelectedRows((prev) => prev.filter((id) => id !== params.row.id));
            if (selectAllMode) setSelectAllMode(false);
          }
        }}
      />
    ),
  };

  const columns = isCoordinator
    ? [...baseColumns, ...coordinatorOnlyColumns]
    : [selectColumn, baseColumns[0], contractNoColumn, ...baseColumns.slice(1), ...adminExtraColumns];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
    setSelectedRows([]);
    setSelectAllMode(false);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header
          title="BOOKINGS"
          subtitle={isCoordinator ? 'Bookings (Passenger, Guide, Destination, Totals)' : 'All Bookings'}
        />

        {!isCoordinator && (
          <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center">
            {selectedRows.length > 0 && can('bookings.delete') && (
              <Button
                sx={{
                  backgroundColor: colors.redAccent[500],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  '&:hover': { backgroundColor: colors.redAccent[600] },
                }}
                variant="contained"
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                <DeleteIcon sx={{ mr: '10px' }} />
                Delete Selected ({selectedRows.length})
              </Button>
            )}
            {can('bookings.create') && (
              <Link to={'/booking/import'}>
                <Button
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    marginRight: '10px',
                    '&:hover': { backgroundColor: colors.blueAccent[800] },
                  }}
                >
                  <DownloadIcon sx={{ mr: '10px' }} />
                  Import Bookings
                </Button>
              </Link>
            )}
            {can('bookings.create') && (
              <Link to={'/booking/addBooking'}>
                <Button
                  sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '10px 20px',
                    '&:hover': { backgroundColor: colors.blueAccent[800] },
                  }}
                >
                  <CalendarTodayIcon sx={{ mr: '10px' }} />
                  Add Booking
                </Button>
              </Link>
            )}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by passenger, contract no, destination..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '340px', backgroundColor: colors.primary[400] }}
        />
        <TextField
          type="date"
          size="small"
          label="Booking Date"
          value={bookingDateFilter}
          onChange={(e) => {
            setBookingDateFilter(e.target.value);
            setPage(1);
            setSelectedRows([]);
            setSelectAllMode(false);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ ml: '20px', minWidth: 200, backgroundColor: colors.primary[400] }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="guide-filter-label">Guide</InputLabel>
          <Select
            labelId="guide-filter-label"
            id="guide-filter"
            value={guideFilter}
            label="Guide"
            onChange={(e) => {
              setGuideFilter(e.target.value);
              setPage(1);
              setSelectedRows([]);
              setSelectAllMode(false);
            }}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            sx={{
              backgroundColor: colors.primary[400],
              color: colors.grey[100],
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
            }}
          >
            <MenuItem value="">
              <em>All guides</em>
            </MenuItem>
            {guides.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {[g.name, g.surname].filter(Boolean).join(' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {(() => {
        const activeGuideFilter = guideFilter
          ? guides.find((g) => String(g.id) === String(guideFilter))
          : null;
        const hasActiveFilters = Boolean(search) || Boolean(bookingDateFilter) || activeGuideFilter;

        if (!hasActiveFilters) return null;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
              Active filters:
            </Typography>
            {search && (
              <Chip
                label={`Search: ${search}`}
                size="small"
                onDelete={handleClearSearch}
                sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
              />
            )}
            {bookingDateFilter && (
              <Chip
                label={`Booking Date: ${bookingDateFilter}`}
                size="small"
                onDelete={() => {
                  setBookingDateFilter('');
                  setPage(1);
                  setSelectedRows([]);
                  setSelectAllMode(false);
                }}
                sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
              />
            )}
            {activeGuideFilter && (
              <Chip
                label={`Guide: ${[activeGuideFilter.name, activeGuideFilter.surname].filter(Boolean).join(' ')}`}
                size="small"
                onDelete={() => {
                  setGuideFilter('');
                  setPage(1);
                  setSelectedRows([]);
                  setSelectAllMode(false);
                }}
                sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
              />
            )}
            {!loading && (
              <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
                {totalCount} result{totalCount !== 1 ? 's' : ''}
              </Typography>
            )}
            <Typography
              variant="body2"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setBookingDateFilter('');
                setGuideFilter('');
                setPage(1);
                setSelectedRows([]);
                setSelectAllMode(false);
              }}
              sx={{
                color: colors.redAccent[500],
                cursor: 'pointer',
                ml: 1,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Clear all
            </Typography>
          </Box>
        );
      })()}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.greenAccent[300] },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: colors.blueAccent[700],
            borderBottom: 'none',
          },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
            backgroundColor: colors.blueAccent[700],
          },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
            color: `${colors.grey[100]} !important`,
            fontSize: 14,
          },
          '& .MuiDataGrid-overlay': {
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          },
          '& .MuiDataGrid-overlay .MuiCircularProgress-root': {
            color: colors.grey[100],
          },
        }}
      >
        <DataGrid
          rows={allBookings}
          columns={filterActionColumns(columns, { Edit: 'bookings.edit', Delete: 'bookings.delete' })}
          getRowId={(row) => row.id}
          components={{ LoadingOverlay: BookingsLoadingOverlay }}
          disableColumnFilter
          page={page - 1}
          pageSize={pageSize}
          sortingOrder={['asc', 'desc']}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          paginationMode="server"
          sortingMode="server"
          rowCount={totalCount}
          loading={loading}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteBooking}
        title="Delete booking?"
        message="Are you sure you want to delete this booking?"
        colors={colors}
      />

      <ConfirmDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedRows.length} booking(s)?`}
        message={`Are you sure you want to delete ${selectedRows.length} selected booking(s)? This action cannot be undone.`}
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default AllBookings;
